import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ClpCopConfig,
  ClpLiveRates,
  ClpPreview,
  ClpPreviewItem,
  ClpTasaSource,
  ColombiaRail,
  ColombiaService,
  LiveRates,
} from '../../services/colombia.service';

/** Form de la pata CLP→USD (singleton — la pata USD→COP se configura aparte). */
interface ClpForm {
  activo: boolean;
  tasaSource: ClpTasaSource;
  /** Solo MANUAL: tasa CLP por USD. */
  tasaManual: number | null;
  /** Solo MANUAL: vencimiento (datetime-local). */
  tasaManualVigenteHasta: string | null;
  motivo: string;
}

const DEFAULT_FORM: ClpForm = {
  activo: false,
  tasaSource: 'LIVE_SELL',
  tasaManual: null,
  tasaManualVigenteHasta: null,
  motivo: '',
};

/** Mínimo del rail (USD), igual que USD→COP. */
const MIN_USD = 20;
/** Montos CLP de la tabla de preview (≈ 20 / 55 / 110 / 550 USD a ~914). */
const PREVIEW_MONTOS_CLP = [20000, 50000, 100000, 500000];

export interface RailClpCopConfigData {
  countryCode?: string; // 'CO'
  countryName?: string;
}

/**
 * MODAL "CLP → COP — Configuración" (pata CLP→USD).
 *
 * El cliente chileno paga con su saldo CLP y el beneficiario en CO recibe COP —
 * ve UNA sola tasa COP/CLP. Acá se gobierna solo la conversión de entrada
 * CLP→USD (fuente de tasa + activación); la pata USD→COP (Vita, ESPEJO) se
 * configura en "USD/COP". La tabla de preview usa el mismo motor del backend.
 */
@Component({
  selector: 'app-rail-clp-cop-config',
  templateUrl: './rail-clp-cop-config.component.html',
  styleUrls: ['./rail-clp-cop-config.component.sass'],
})
export class RailClpCopConfigComponent implements OnInit, OnDestroy {
  readonly rails: ColombiaRail[] = ['COBREB', 'CO'];
  readonly railLabel: Record<ColombiaRail, string> = {
    COBREB: 'Bre-B (instantáneo)',
    CO: 'Transferencia (1 día)',
  };
  // Spot NO es opción seleccionable (el operador no la elige a mano); queda solo
  // como fallback interno del backend si la venta viva se cae.
  readonly sources: { value: ClpTasaSource; label: string; hint: string }[] = [
    { value: 'LIVE_SELL', label: 'Venta Aguacate (vivo)', hint: 'La misma tasa que el cliente ve hoy en CLP→USDT' },
    { value: 'MANUAL', label: 'Manual (override)', hint: 'Tasa fija con vencimiento' },
  ];

  config: ClpCopConfig | null = null;
  live: ClpLiveRates | null = null;
  /** Tasas vivas de la pata USD→COP (Vita) — para mostrar la tasa Vita actual. */
  usdLive: LiveRates | null = null;

  form: ClpForm = { ...DEFAULT_FORM };

  preview: ClpPreview | null = null;
  previewLoading = false;
  private previewTimer: any = null;
  private previewSeq = 0;

  /** USD→COP (master). Si OFF, no se puede ofrecer CLP→COP (va por ese puente). */
  usdCopOfrecido = true;
  loading = false;
  saving = false;
  error: string | null = null;
  okMsg: string | null = null;

  constructor(
    private svc: ColombiaService,
    public dialogRef: MatDialogRef<RailClpCopConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RailClpCopConfigData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const [config, live, usdLive, railes] = await Promise.all([
        this.svc.getClpConfig(),
        this.svc.getClpLive(),
        this.svc.getLive().catch(() => null),
        this.svc.getRailesActivos().catch(() => null),
      ]);
      this.config = config;
      this.live = live;
      this.usdLive = usdLive;
      this.fromConfig(config);
      // El on/off vive en el switchboard (parametros), no en la config de precio.
      this.form.activo = !!railes?.cop?.clp_cop;
      // usd_cop es el MASTER de COP: CLP→COP va por el puente USD→COP, así que
      // no se puede ofrecer CLP→COP si USD→COP está apagado.
      this.usdCopOfrecido = railes?.cop?.usdt_cop !== false;
      if (!this.usdCopOfrecido) this.form.activo = false;
      this.refreshPreview(true);
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo cargar la configuración';
    } finally {
      this.loading = false;
    }
  }

  private fromConfig(c: ClpCopConfig | null): void {
    if (!c) {
      this.form = { ...DEFAULT_FORM };
      return;
    }
    this.form = {
      activo: false, // lo setea load() desde el switchboard
      tasaSource: c.tasaSource,
      tasaManual: c.tasaManual != null ? Number(c.tasaManual) : null,
      tasaManualVigenteHasta: c.tasaManualVigenteHasta
        ? c.tasaManualVigenteHasta.slice(0, 16)
        : null,
      motivo: '',
    };
  }

  onFormChange(): void {
    this.okMsg = null;
    this.refreshPreview();
  }

  /** Toggle "Ofrecer CLP→COP". Bloqueado si USD→COP (master) está OFF. */
  toggleActivo(): void {
    if (!this.usdCopOfrecido) return;
    this.form.activo = !this.form.activo;
    this.onFormChange();
  }

  refreshPreview(inmediato = false): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.fetchPreview(), inmediato ? 0 : 600);
  }

  private async fetchPreview(): Promise<void> {
    const seq = ++this.previewSeq;
    this.previewLoading = true;
    try {
      const p = await this.svc.clpQuotePreview(PREVIEW_MONTOS_CLP, {
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManual != null
            ? Number(this.form.tasaManual)
            : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
      });
      if (seq === this.previewSeq) this.preview = p;
    } catch {
      if (seq === this.previewSeq) this.preview = null;
    } finally {
      if (seq === this.previewSeq) this.previewLoading = false;
    }
  }

  async save(): Promise<void> {
    if (this.form.tasaSource === 'MANUAL') {
      if (!(Number(this.form.tasaManual) > 0) || !this.form.tasaManualVigenteHasta) {
        this.error = 'MANUAL requiere tasa y vencimiento futuros.';
        return;
      }
    }
    this.saving = true;
    this.error = null;
    this.okMsg = null;
    try {
      // 1) On/off del rail (switchboard en parametros).
      await this.svc.setRailesActivos({ clpCop: !!this.form.activo });
      // 2) Config de PRECIO (entity versionada) — sin el on/off.
      await this.svc.upsertClpConfig({
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' ? Number(this.form.tasaManual) : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        motivo: this.form.motivo || undefined,
      });
      this.okMsg = `Configuración guardada (${this.form.activo ? 'CLP→COP activo' : 'CLP→COP inactivo'}).`;
      await this.load();
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo guardar';
      await this.load();
    } finally {
      this.saving = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Helpers de vista ──

  itemsDe(rail: ColombiaRail): ClpPreviewItem[] {
    return this.preview?.rails?.[rail] ?? [];
  }

  /** Tasa CLP/USD que se está usando (de la fuente activa). */
  get tasaClpUsd(): number | null {
    return this.preview?.rate?.efectiva ?? this.live?.rate?.efectiva ?? null;
  }
  get tasaStale(): boolean {
    return !!(this.preview?.rate?.stale ?? this.live?.rate?.stale);
  }
  get sourceResuelta(): ClpTasaSource | null {
    return this.preview?.rate?.source ?? this.live?.rate?.source ?? null;
  }

  /** Mínimo (20 USD) expresado en CLP a la tasa actual. */
  get minClpEquivalente(): number | null {
    const r = this.tasaClpUsd;
    return r && r > 0 ? Math.ceil(MIN_USD * r) : null;
  }

  // ── Tasa Vita actual (pata de salida USD→COP) ──
  vitaRetailCop(rail: ColombiaRail): number | null {
    return this.usdLive?.rails?.[rail]?.publicSellUsd ?? this.usdLive?.rails?.[rail]?.publicUsdSell ?? null;
  }
  vitaPartnerCop(rail: ColombiaRail): number | null {
    return this.usdLive?.rails?.[rail]?.partnerUsdSell ?? null;
  }
  vitaFeeCop(rail: ColombiaRail): number | null {
    return this.usdLive?.rails?.[rail]?.fixedCostCop ?? null;
  }

  private allItems(): ClpPreviewItem[] {
    return this.rails.flatMap((r) => this.itemsDe(r));
  }

  // ── Alertas (espejo de las del modal USD→COP) ──

  get alertSinMargen(): boolean {
    return this.allItems().some((i) => i.cumpleMinimo && i.disponible && !i.margenOk);
  }
  get alertBajoVita(): boolean {
    return this.allItems().some(
      (i) => i.clamped || (i.ofertaVitaCop != null && i.recibeCop < i.ofertaVitaCop),
    );
  }
  get alertEspejoDegradado(): boolean {
    return this.allItems().some((i) => i.espejoFallback);
  }
  get alertFeedStale(): boolean {
    return this.tasaStale || this.allItems().some((i) => i.stale);
  }
}
